import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Tag, Space, Button, Typography, Spin, Empty, Modal,
  Input, Drawer, Timeline, Divider, message, Radio, Badge, Tabs, InputNumber,
  Form, Switch,
} from 'antd';
import {
  TrophyOutlined, ClockCircleOutlined, UserOutlined, DollarOutlined,
  SendOutlined, CheckCircleOutlined, PlayCircleOutlined, RollbackOutlined,
  FileTextOutlined, LinkOutlined, MinusCircleOutlined, PlusOutlined,
} from '@ant-design/icons';
import { taskAPI } from '../services/api';
import type { ValidationTask, TaskMilestone } from '../types';
import FileUploader from '../components/FileUploader';
import dayjs from 'dayjs';
import { formatCN } from '../utils/dateFormat';
import { getCanonicalModelDisplayName } from '../utils/modelDisplay';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const STATUS_CONFIG: Record<string, { color: string; label: string }> = {
  draft: { color: 'default', label: '草稿' },
  published: { color: 'blue', label: '招募中' },
  assigned: { color: 'cyan', label: '已指派' },
  in_progress: { color: 'orange', label: '执行中' },
  submitted: { color: 'purple', label: '已提交' },
  reviewing: { color: 'geekblue', label: '审核中' },
  completed: { color: 'green', label: '已完成' },
  cancelled: { color: 'default', label: '已取消' },
  expired: { color: 'default', label: '已过期' },
};

const TaskMarketplace: React.FC = () => {
  const [tasks, setTasks] = useState<ValidationTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showMyTasks, setShowMyTasks] = useState(false);

  // Detail drawer
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState<ValidationTask | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Apply modal
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyTaskId, setApplyTaskId] = useState<string | null>(null);
  const [applyExperience, setApplyExperience] = useState('');
  const [applyCredentials, setApplyCredentials] = useState('');
  const [applyLoading, setApplyLoading] = useState(false);

  // Submit modal
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitTaskId, setSubmitTaskId] = useState<string | null>(null);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitForm] = Form.useForm();

  const fetchTasks = async () => {
    setLoading(true);
    try {
      if (showMyTasks) {
        const res = await taskAPI.myTasks();
        setTasks(res.tasks || []);
      } else {
        const params: Record<string, any> = { size: 100 };
        if (statusFilter !== 'all') params.status = statusFilter;
        const res = await taskAPI.list(params);
        setTasks(res.tasks || []);
      }
    } catch {
      message.error('加载任务失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTasks(); }, [statusFilter, showMyTasks]);

  const openDetail = async (taskId: string) => {
    setDetailOpen(true);
    setDetailLoading(true);
    try {
      const data = await taskAPI.detail(taskId);
      setDetailData(data);
    } catch {
      message.error('加载任务详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleApply = async () => {
    if (!applyTaskId || !applyExperience.trim()) return;
    setApplyLoading(true);
    try {
      await taskAPI.apply(applyTaskId, {
        experience: applyExperience,
        credentials: applyCredentials || undefined,
      });
      message.success('竞选申请已提交');
      setApplyOpen(false);
      setApplyExperience('');
      setApplyCredentials('');
      if (detailData?.id === applyTaskId) openDetail(applyTaskId);
      fetchTasks();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '申请提交失败');
    } finally {
      setApplyLoading(false);
    }
  };

  const handleWithdraw = async (taskId: string) => {
    try {
      await taskAPI.withdrawApplication(taskId);
      message.success('申请已撤回');
      if (detailData?.id === taskId) openDetail(taskId);
      fetchTasks();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '撤回失败');
    }
  };

  const handleStart = async (taskId: string) => {
    try {
      await taskAPI.start(taskId);
      message.success('任务已开始');
      openDetail(taskId);
      fetchTasks();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '操作失败');
    }
  };

  const handleSubmit = async () => {
    if (!submitTaskId) return;
    setSubmitLoading(true);
    try {
      const values = submitForm.getFieldsValue(true);
      const report: Record<string, any> = {
        summary: values.summary || '',
        gof: {
          dv_vs_pred_description: values.gof_dv_vs_pred || '',
          dv_vs_pred_images: values.gof_dv_vs_pred_images || [],
          dv_vs_ipred_description: values.gof_dv_vs_ipred || '',
          dv_vs_ipred_images: values.gof_dv_vs_ipred_images || [],
          cwres_description: values.gof_cwres || '',
          cwres_images: values.gof_cwres_images || [],
          conclusion: values.gof_conclusion || '',
        },
        stability: {
          bootstrap_n: values.bootstrap_n ?? null,
          bootstrap_success_rate: values.bootstrap_success_rate ?? null,
          parameter_bias_percent: values.parameter_bias_percent ?? null,
          conclusion: values.stability_conclusion || '',
        },
        predictive_ability: {
          vpc_description: values.vpc_description || '',
          vpc_images: values.vpc_images || [],
          npde_mean: values.npde_mean ?? null,
          npde_variance: values.npde_variance ?? null,
          npde_normality_p: values.npde_normality_p ?? null,
          conclusion: values.pred_conclusion || '',
        },
        error_metrics: {
          mpe: values.mpe ?? null,
          mpe_percent: values.mpe_percent ?? null,
          mae: values.mae ?? null,
          mape: values.mape ?? null,
          rmse: values.rmse ?? null,
          conclusion: values.error_conclusion || '',
        },
        parameter_evaluation: {
          parameters: (values.parameters || []).filter((p: any) => p?.name),
          overall_rse_acceptable: values.rse_acceptable ?? null,
          overall_shrinkage_acceptable: values.shrinkage_acceptable ?? null,
          conclusion: values.param_conclusion || '',
        },
        overall_conclusion: values.overall_conclusion || '',
        pk_data_files: values.pk_data_files || [],
        model_code_files: values.model_code_files || [],
        other_attachments: values.other_attachments || [],
      };
      await taskAPI.submit(submitTaskId, {
        submission_text: report.summary,
        submission_report: report,
      });
      message.success('成果已提交');
      setSubmitOpen(false);
      submitForm.resetFields();
      if (detailData?.id === submitTaskId) openDetail(submitTaskId);
      fetchTasks();
    } catch (err: any) {
      message.error(err?.response?.data?.detail || '提交失败');
    } finally {
      setSubmitLoading(false);
    }
  };

  const renderDeadline = (deadline?: string) => {
    if (!deadline) return null;
    const d = dayjs(deadline);
    const diff = d.diff(dayjs(), 'day');
    return (
      <span>
        <ClockCircleOutlined style={{ marginRight: 4 }} />
        {d.format('YYYY-MM-DD')}
        {diff >= 0 && diff <= 7 && <Text type="danger" style={{ marginLeft: 4 }}>({diff}天后截止)</Text>}
      </span>
    );
  };

  const renderReferenceInfo = (ref?: Record<string, any>) => {
    if (!ref || (!ref.pmid && !ref.doi && !ref.journal)) return null;
    return (
      <Card size="small" title={<><LinkOutlined /> 参考文献</>} style={{ marginBottom: 12 }}>
        <Space direction="vertical" size="small">
          {ref.first_author && ref.year && (
            <Text>{ref.first_author} ({ref.year})</Text>
          )}
          {ref.journal && <Text type="secondary">期刊: {ref.journal}</Text>}
          {ref.pmid && (
            <Text>
              PMID:{' '}
              <a href={`https://pubmed.ncbi.nlm.nih.gov/${ref.pmid}/`} target="_blank" rel="noopener noreferrer">
                {ref.pmid}
              </a>
            </Text>
          )}
          {ref.doi && (
            <Text>
              DOI:{' '}
              <a href={`https://doi.org/${ref.doi}`} target="_blank" rel="noopener noreferrer">
                {ref.doi}
              </a>
            </Text>
          )}
        </Space>
      </Card>
    );
  };

  const renderTaskCard = (task: ValidationTask) => {
    const sc = STATUS_CONFIG[task.status] || STATUS_CONFIG.draft;
    return (
      <Col xs={24} sm={12} lg={8} key={task.id}>
        <Card
          hoverable
          style={{ height: '100%' }}
          actions={[
            <Button type="link" onClick={() => openDetail(task.id)}>
              查看详情
            </Button>,
          ]}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Space>
              <Tag color={sc.color}>{sc.label}</Tag>
              <Text strong style={{ fontSize: 15 }}>{task.title}</Text>
            </Space>
            <div>
              <Text type="secondary">关联模型: </Text>
              <Text>{getCanonicalModelDisplayName({
                modelId: task.model_id,
                modelName: task.model_name,
                firstAuthor: task.reference_info?.first_author,
                year: task.reference_info?.year,
              })}</Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space>
                <DollarOutlined style={{ color: '#C5A55A' }} />
                <Text strong style={{ color: '#C5A55A', fontSize: 16 }}>{task.reward_coins}</Text>
                <Text type="secondary">金币</Text>
              </Space>
              <Space>
                <UserOutlined />
                <Text type="secondary">{task.application_count || 0} 人申请</Text>
              </Space>
            </div>
            {task.deadline && (
              <div style={{ color: '#8c8c8c', fontSize: 12 }}>
                {renderDeadline(task.deadline)}
              </div>
            )}
          </Space>
        </Card>
      </Col>
    );
  };

  const renderMilestoneTimeline = (milestones: TaskMilestone[]) => {
    if (!milestones || milestones.length === 0) return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无事件" />;
    return (
      <Timeline
        items={milestones.map((ms) => ({
          color: ms.event_type.includes('cancel') || ms.event_type.includes('reject') ? 'red'
            : ms.event_type.includes('approved') || ms.event_type.includes('completed') ? 'green'
              : 'blue',
          children: (
            <div>
              <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                {formatCN(ms.created_at, 'YYYY-MM-DD HH:mm')}
              </Text>
              <div>{ms.event_description}</div>
            </div>
          ),
        }))}
      />
    );
  };

  const renderDetailActions = () => {
    if (!detailData) return null;
    const myApp = detailData.my_application;

    return (
      <Space wrap>
        {detailData.status === 'published' && !myApp && (
          <Button type="primary" icon={<SendOutlined />} onClick={() => {
            setApplyTaskId(detailData.id);
            setApplyOpen(true);
          }}>
            我要竞选
          </Button>
        )}
        {myApp?.status === 'pending' && (
          <Button icon={<RollbackOutlined />} onClick={() => handleWithdraw(detailData.id)}>
            撤回申请
          </Button>
        )}
        {detailData.status === 'assigned' && myApp?.status === 'approved' && (
          <Button type="primary" icon={<PlayCircleOutlined />} onClick={() => handleStart(detailData.id)}>
            确认开始
          </Button>
        )}
        {detailData.status === 'in_progress' && myApp?.status === 'approved' && (
          <Button type="primary" icon={<FileTextOutlined />} onClick={() => {
            setSubmitTaskId(detailData.id);
            submitForm.resetFields();
            setSubmitOpen(true);
          }}>
            提交成果
          </Button>
        )}
        {myApp && (
          <Tag color={
            myApp.status === 'approved' ? 'green'
              : myApp.status === 'rejected' ? 'red'
                : myApp.status === 'pending' ? 'orange'
                  : 'default'
          }>
            我的申请: {
              myApp.status === 'approved' ? '已通过'
                : myApp.status === 'rejected' ? '未通过'
                  : myApp.status === 'pending' ? '待审核'
                    : '已撤回'
            }
          </Tag>
        )}
      </Space>
    );
  };

  const submitTabItems = [
    {
      key: 'basic',
      label: '基本信息',
      children: (
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Form.Item name="summary" label="验证总结">
            <TextArea rows={4} placeholder="请描述验证过程和主要结论..." />
          </Form.Item>
          <Form.Item name="pk_data_files" label="PK 数据文件 (.csv / .xlsx)">
            <FileUploader category="pk_data" maxCount={5} />
          </Form.Item>
          <Form.Item name="model_code_files" label="模型代码 (.R / .ctl / .mod / .mlx)">
            <FileUploader category="model_code" maxCount={10} />
          </Form.Item>
          <Form.Item name="other_attachments" label="其他附件">
            <FileUploader category="attachment" maxCount={10} />
          </Form.Item>
        </Space>
      ),
    },
    {
      key: 'gof',
      label: '拟合优度',
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Form.Item name="gof_dv_vs_pred" label="DV vs PRED 描述">
            <TextArea rows={2} placeholder="DV与PRED散点分布情况..." />
          </Form.Item>
          <Form.Item name="gof_dv_vs_pred_images" label="DV vs PRED 图片">
            <FileUploader category="image" maxCount={3} />
          </Form.Item>
          <Form.Item name="gof_dv_vs_ipred" label="DV vs IPRED 描述">
            <TextArea rows={2} placeholder="DV与IPRED拟合情况..." />
          </Form.Item>
          <Form.Item name="gof_dv_vs_ipred_images" label="DV vs IPRED 图片">
            <FileUploader category="image" maxCount={3} />
          </Form.Item>
          <Form.Item name="gof_cwres" label="CWRES 描述">
            <TextArea rows={2} placeholder="CWRES分布情况..." />
          </Form.Item>
          <Form.Item name="gof_cwres_images" label="CWRES 图片">
            <FileUploader category="image" maxCount={3} />
          </Form.Item>
          <Form.Item name="gof_conclusion" label="结论">
            <TextArea rows={1} placeholder="拟合优度结论" />
          </Form.Item>
        </Space>
      ),
    },
    {
      key: 'stability',
      label: '稳定性',
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="bootstrap_n" label="Bootstrap 次数">
                <InputNumber style={{ width: '100%' }} min={0} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="bootstrap_success_rate" label="成功率 (%)">
                <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="parameter_bias_percent" label="参数偏差 (%)">
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="stability_conclusion" label="结论">
            <TextArea rows={1} placeholder="稳定性评价结论" />
          </Form.Item>
        </Space>
      ),
    },
    {
      key: 'prediction',
      label: '预测能力',
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Form.Item name="vpc_description" label="VPC 描述">
            <TextArea rows={2} placeholder="VPC结果描述..." />
          </Form.Item>
          <Form.Item name="vpc_images" label="VPC 图片">
            <FileUploader category="image" maxCount={3} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="npde_mean" label="NPDE 均值">
                <InputNumber style={{ width: '100%' }} step={0.01} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="npde_variance" label="NPDE 方差">
                <InputNumber style={{ width: '100%' }} step={0.01} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="npde_normality_p" label="正态性 p 值">
                <InputNumber style={{ width: '100%' }} step={0.01} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="pred_conclusion" label="结论">
            <TextArea rows={1} placeholder="预测能力结论" />
          </Form.Item>
        </Space>
      ),
    },
    {
      key: 'errors',
      label: '误差评价',
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="mpe" label="MPE">
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mpe_percent" label="MPE%">
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="mae" label="MAE">
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="mape" label="MAPE">
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="rmse" label="RMSE">
                <InputNumber style={{ width: '100%' }} step={0.1} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="error_conclusion" label="结论">
            <TextArea rows={1} placeholder="误差评价结论" />
          </Form.Item>
        </Space>
      ),
    },
    {
      key: 'params',
      label: '参数评价',
      children: (
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Form.List name="parameters">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <Row gutter={8} key={key} align="middle">
                    <Col span={6}>
                      <Form.Item {...rest} name={[name, 'name']} noStyle>
                        <Input placeholder="参数名 (如 CL)" />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item {...rest} name={[name, 'estimate']} noStyle>
                        <InputNumber placeholder="估计值" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item {...rest} name={[name, 'rse_percent']} noStyle>
                        <InputNumber placeholder="RSE%" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={5}>
                      <Form.Item {...rest} name={[name, 'shrinkage_percent']} noStyle>
                        <InputNumber placeholder="Shrinkage%" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ marginTop: 4 }}>
                  添加参数
                </Button>
              </>
            )}
          </Form.List>
          <Row gutter={16} style={{ marginTop: 8 }}>
            <Col span={12}>
              <Form.Item name="rse_acceptable" label="RSE 整体可接受" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="shrinkage_acceptable" label="Shrinkage 整体可接受" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="param_conclusion" label="结论">
            <TextArea rows={1} placeholder="参数评价结论" />
          </Form.Item>
        </Space>
      ),
    },
    {
      key: 'conclusion',
      label: '总体结论',
      children: (
        <Form.Item name="overall_conclusion" label="总体结论">
          <TextArea rows={4} placeholder="模型验证总体结论..." />
        </Form.Item>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          <TrophyOutlined /> 揭榜领金
        </Title>
        <Button
          type={showMyTasks ? 'primary' : 'default'}
          onClick={() => setShowMyTasks(!showMyTasks)}
        >
          {showMyTasks ? '返回任务广场' : '我的任务'}
        </Button>
      </div>

      {!showMyTasks && (
        <Radio.Group
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ marginBottom: 16 }}
          buttonStyle="solid"
        >
          <Radio.Button value="all">全部</Radio.Button>
          <Radio.Button value="published">招募中</Radio.Button>
          <Radio.Button value="in_progress">进行中</Radio.Button>
          <Radio.Button value="completed">已完成</Radio.Button>
        </Radio.Group>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}>
          <Spin size="large" />
        </div>
      ) : tasks.length === 0 ? (
        <Empty description={showMyTasks ? '您暂未参与任何任务' : '暂无任务'} />
      ) : (
        <Row gutter={[16, 16]}>
          {tasks.map(renderTaskCard)}
        </Row>
      )}

      {/* Task Detail Drawer */}
      <Drawer
        title={detailData?.title || '任务详情'}
        placement="right"
        width={640}
        open={detailOpen}
        onClose={() => { setDetailOpen(false); setDetailData(null); }}
        destroyOnClose
      >
        {detailLoading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
        ) : detailData ? (
          <div>
            <Space style={{ marginBottom: 16 }} wrap>
              <Tag color={STATUS_CONFIG[detailData.status]?.color}>
                {STATUS_CONFIG[detailData.status]?.label}
              </Tag>
              <Space>
                <DollarOutlined style={{ color: '#C5A55A' }} />
                <Text strong style={{ color: '#C5A55A', fontSize: 18 }}>{detailData.reward_coins}</Text>
                <Text type="secondary">金币</Text>
              </Space>
              <Text type="secondary">
                <UserOutlined /> {detailData.application_count || 0} 人申请
              </Text>
            </Space>

            {/* Reference Info */}
            {renderReferenceInfo(detailData.reference_info)}

            {detailData.description && (
              <>
                <Title level={5}>任务描述</Title>
                <Paragraph>{detailData.description}</Paragraph>
              </>
            )}

            <Title level={5}>完成要求</Title>
            <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{detailData.requirements}</Paragraph>

            {detailData.deliverables && (
              <>
                <Title level={5}>交付物说明</Title>
                <Paragraph>{detailData.deliverables}</Paragraph>
              </>
            )}

            <Space direction="vertical" size="small" style={{ marginBottom: 16 }}>
              <Text type="secondary">关联模型: {getCanonicalModelDisplayName({
                modelId: detailData.model_id,
                modelName: detailData.model_name,
                firstAuthor: detailData.reference_info?.first_author,
                year: detailData.reference_info?.year,
              })}</Text>
              {detailData.deadline && <Text type="secondary">截止日期: {formatCN(detailData.deadline, 'YYYY-MM-DD')}</Text>}
              {detailData.assigned_user_name && <Text type="secondary">执行人: {detailData.assigned_user_name}</Text>}
              {detailData.creator_name && <Text type="secondary">发布人: {detailData.creator_name}</Text>}
            </Space>

            {detailData.submission_text && (
              <>
                <Divider />
                <Title level={5}>提交的成果</Title>
                <Paragraph style={{ whiteSpace: 'pre-wrap' }}>{detailData.submission_text}</Paragraph>
              </>
            )}

            {detailData.review_comment && (
              <>
                <Title level={5}>审核意见</Title>
                <Paragraph>{detailData.review_comment}</Paragraph>
              </>
            )}

            <Divider />
            {renderDetailActions()}

            <Divider />
            <Title level={5}>任务里程碑</Title>
            {renderMilestoneTimeline(detailData.milestones || [])}
          </div>
        ) : null}
      </Drawer>

      {/* Apply Modal */}
      <Modal
        title="竞选申请"
        open={applyOpen}
        onOk={handleApply}
        onCancel={() => { setApplyOpen(false); setApplyExperience(''); setApplyCredentials(''); }}
        confirmLoading={applyLoading}
        okText="提交申请"
        okButtonProps={{ disabled: !applyExperience.trim() }}
      >
        <div style={{ marginBottom: 12 }}>
          <Text strong>建模经历 *</Text>
          <TextArea
            rows={5}
            value={applyExperience}
            onChange={(e) => setApplyExperience(e.target.value)}
            placeholder="请描述您的群体药动学建模经历、使用过的软件和工具、发表的相关论文..."
          />
        </div>
        <div>
          <Text strong>成果/资质证明</Text>
          <TextArea
            rows={3}
            value={applyCredentials}
            onChange={(e) => setApplyCredentials(e.target.value)}
            placeholder="（选填）证明您具备完成该任务能力的材料说明"
          />
        </div>
      </Modal>

      {/* Structured Submit Modal */}
      <Modal
        title="提交验证成果报告"
        open={submitOpen}
        onOk={handleSubmit}
        onCancel={() => { setSubmitOpen(false); submitForm.resetFields(); }}
        confirmLoading={submitLoading}
        okText="提交成果"
        width={720}
        destroyOnClose
      >
        <Form form={submitForm} layout="vertical" size="small">
          <Tabs items={submitTabItems} size="small" />
        </Form>
      </Modal>
    </div>
  );
};

export default TaskMarketplace;
